import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { fileUrl } from "@/lib/files";
import { toast } from "sonner";
import { Package, MessageSquare, Store, ShoppingBag } from "lucide-react";
import RatingStars from "@/components/RatingStars";
import TierBadge from "@/components/TierBadge";
import { useAuth } from "@/contexts/AuthContext";

export default function BuyerProduct() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [supplierRating, setSupplierRating] = useState(null);
  const [selectedImg, setSelectedImg] = useState(0);
  const [q, setQ] = useState({ quantity: 1, target_price: "", destination_country: "", incoterms: "FOB", notes: "" });
  const [busy, setBusy] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgBody, setMsgBody] = useState("");
  const [msgBusy, setMsgBusy] = useState(false);
  const [supplierTier, setSupplierTier] = useState(null);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [sample, setSample] = useState({ quantity: 1, shipping_name: "", shipping_address: "", shipping_city: "", shipping_country: "", shipping_postal: "", phone: "", notes: "" });
  const [sampleBusy, setSampleBusy] = useState(false);

  useEffect(() => { api.get(`/products/${pid}`).then((r) => setP(r.data)).catch(() => toast.error("Not found")); }, [pid]);
  useEffect(() => {
    if (p?.supplier_id) api.get(`/suppliers/${p.supplier_id}`).then((r) => { setSupplierRating(r.data.rating); setSupplierTier(r.data.tier); }).catch(() => {});
  }, [p?.supplier_id]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/quotes", {
        product_id: pid,
        quantity: parseInt(q.quantity) || 1,
        target_price: q.target_price ? parseFloat(q.target_price) : null,
        incoterms: q.incoterms, destination_country: q.destination_country, notes: q.notes,
      });
      toast.success("Quote request sent");
      nav("/buyer/quotes");
    } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendMessage = async () => {
    if (!msgBody.trim() || !p?.supplier_id) return;
    setMsgBusy(true);
    try {
      const { data } = await api.post("/threads", {
        subject: `Question about ${p.name}`,
        body: msgBody,
        recipient_role: "supplier",
        recipient_id: p.supplier_id,
        product_id: p.id,
      });
      toast.success("Message sent — admin CC'd for visibility");
      setMsgOpen(false); setMsgBody("");
      nav(`/messages/${data.thread_id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMsgBusy(false); }
  };

  const orderSample = async () => {
    setSampleBusy(true);
    try {
      const { data } = await api.post("/sample-orders", { product_id: pid, ...sample, quantity: parseInt(sample.quantity) || 1 });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error("No checkout URL");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSampleBusy(false); }
  };

  if (!p) return <DashboardLayout title="Product"><div>Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout title={p.name}>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-6">
          <div className="aspect-[16/10] bg-muted rounded-lg overflow-hidden grid place-items-center">
            {p.image_ids?.[selectedImg] ? (
              <img src={fileUrl(p.image_ids[selectedImg])} alt={p.name} className="w-full h-full object-cover" data-testid="product-main-image" />
            ) : <Package className="w-14 h-14 text-muted-foreground" />}
          </div>
          {p.image_ids?.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {p.image_ids.map((id, i) => (
                <button key={id} onClick={() => setSelectedImg(i)} className={`aspect-square rounded-md overflow-hidden border-2 ${selectedImg === i ? "border-primary" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                  <img src={fileUrl(id)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <Card>
            <CardHeader><CardTitle className="font-heading tracking-tight">About this product</CardTitle></CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">{p.description}</p>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><div className="overline text-muted-foreground">Origin</div><div className="mt-1 text-secondary">{p.origin_country}</div></div>
                <div><div className="overline text-muted-foreground">HS code</div><div className="mt-1 text-secondary">{p.hs_code || "—"}</div></div>
                <div><div className="overline text-muted-foreground">MOQ</div><div className="mt-1 text-secondary">{p.moq} {p.unit}</div></div>
                <div><div className="overline text-muted-foreground">Lead time</div><div className="mt-1 text-secondary">{p.lead_time_days ? `${p.lead_time_days} days` : "—"}</div></div>
              </div>
              {p.certifications?.length > 0 && (
                <div className="mt-6">
                  <div className="overline text-muted-foreground mb-2">Certifications</div>
                  <div className="flex flex-wrap gap-2">{p.certifications.map((c) => <Badge key={c} className="bg-primary/10 text-primary border-transparent">{c}</Badge>)}</div>
                </div>
              )}
              {p.supplier && (
                <div className="mt-6 p-4 bg-muted/40 rounded-md flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="overline text-muted-foreground">Supplier</div>
                    <Link to={`/suppliers/${p.supplier_id}`} className="mt-1 font-medium text-secondary hover:text-primary inline-flex items-center gap-1.5" data-testid="visit-storefront">
                      <Store className="w-4 h-4" />{p.supplier.company_name || p.supplier.name}
                    </Link>
                    <div className="text-xs text-muted-foreground">{p.supplier.country}</div>
                    {supplierRating && (
                      <div className="mt-2"><RatingStars value={supplierRating.avg || 0} count={supplierRating.count || 0} testid="supplier-rating-inline" /></div>
                    )}
                  </div>
                  {user?.role === "buyer" && (
                    <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" data-testid="message-supplier-btn"><MessageSquare className="w-3.5 h-3.5 mr-1.5" />Message supplier</Button>
                      </DialogTrigger>
                      <DialogContent data-testid="message-supplier-dialog">
                        <DialogHeader><DialogTitle className="font-heading tracking-tight">Message {p.supplier.company_name || p.supplier.name}</DialogTitle></DialogHeader>
                        <p className="text-xs text-muted-foreground">Your message goes directly to the supplier. The Safeway Finland ops team is automatically CC'd for compliance visibility.</p>
                        <div><Label>Your message</Label><Textarea data-testid="ms-body" rows={5} value={msgBody} onChange={(e) => setMsgBody(e.target.value)} placeholder={`Ask about ${p.name} specs, lead time, custom orders…`} /></div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setMsgOpen(false)}>Cancel</Button>
                          <Button data-testid="ms-send" onClick={sendMessage} disabled={msgBusy || !msgBody.trim()}>{msgBusy ? "Sending…" : "Send"}</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        <div className="lg:col-span-2 space-y-4">
          {user?.role === "buyer" && p.sample_available && p.sample_price && (
            <Card className="card-lift border-primary/40" data-testid="sample-card">
              <CardHeader>
                <div className="overline text-primary">TRY A SAMPLE</div>
                <div className="mt-1 font-heading text-xl font-bold text-secondary tracking-tight">Order {p.sample_quantity || 1} {p.sample_unit || p.unit} for ${parseFloat(p.sample_price).toFixed(2)}</div>
                <div className="text-xs text-muted-foreground mt-1">Small-quantity paid sample. Ship to you before you commit to the full MOQ.</div>
              </CardHeader>
              <CardContent>
                <Dialog open={sampleOpen} onOpenChange={setSampleOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full" data-testid="order-sample-btn"><ShoppingBag className="w-4 h-4 mr-2" />Order sample</Button>
                  </DialogTrigger>
                  <DialogContent data-testid="sample-dialog">
                    <DialogHeader><DialogTitle className="font-heading tracking-tight">Order sample: {p.name}</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div><Label>Quantity (sample units)</Label><Input data-testid="sample-qty" type="number" min="1" value={sample.quantity} onChange={(e) => setSample({ ...sample, quantity: e.target.value })} /></div>
                      <div><Label>Recipient name</Label><Input data-testid="sample-name" value={sample.shipping_name} onChange={(e) => setSample({ ...sample, shipping_name: e.target.value })} /></div>
                      <div className="md:col-span-2"><Label>Shipping address</Label><Input data-testid="sample-addr" value={sample.shipping_address} onChange={(e) => setSample({ ...sample, shipping_address: e.target.value })} /></div>
                      <div><Label>City</Label><Input data-testid="sample-city" value={sample.shipping_city} onChange={(e) => setSample({ ...sample, shipping_city: e.target.value })} /></div>
                      <div><Label>Country</Label><Input data-testid="sample-country" value={sample.shipping_country} onChange={(e) => setSample({ ...sample, shipping_country: e.target.value })} /></div>
                      <div><Label>Postal code</Label><Input data-testid="sample-postal" value={sample.shipping_postal} onChange={(e) => setSample({ ...sample, shipping_postal: e.target.value })} /></div>
                      <div><Label>Phone</Label><Input data-testid="sample-phone" value={sample.phone} onChange={(e) => setSample({ ...sample, phone: e.target.value })} /></div>
                      <div className="md:col-span-2"><Label>Notes</Label><Textarea data-testid="sample-notes" rows={2} value={sample.notes} onChange={(e) => setSample({ ...sample, notes: e.target.value })} /></div>
                      <div className="md:col-span-2 text-right font-heading text-2xl font-black text-secondary">Total: ${((parseFloat(p.sample_price) || 0) * (parseInt(sample.quantity) || 1)).toFixed(2)}</div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setSampleOpen(false)}>Cancel</Button>
                      <Button data-testid="sample-checkout" onClick={orderSample} disabled={sampleBusy || !sample.shipping_name || !sample.shipping_address || !sample.shipping_city || !sample.shipping_country}>{sampleBusy ? "Redirecting…" : "Pay & order"}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          )}
          <Card className="card-lift sticky top-24">
            <CardHeader>
              <div className="text-sm text-muted-foreground">Indicative price</div>
              <div className="font-heading text-3xl font-black text-secondary tracking-tight" data-testid="product-price">{p.price_min ? `${p.price_min}–${p.price_max || "?"} ${p.currency}/${p.unit}` : "On request"}</div>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-3" data-testid="quote-form">
                <div><Label>Quantity ({p.unit})</Label><Input data-testid="quote-qty" type="number" min="1" value={q.quantity} onChange={(e) => setQ({ ...q, quantity: e.target.value })} /></div>
                <div><Label>Target price / {p.unit} (optional)</Label><Input data-testid="quote-target" type="number" step="0.01" value={q.target_price} onChange={(e) => setQ({ ...q, target_price: e.target.value })} /></div>
                <div><Label>Destination country</Label><Input data-testid="quote-dest" value={q.destination_country} onChange={(e) => setQ({ ...q, destination_country: e.target.value })} /></div>
                <div>
                  <Label>Incoterms</Label>
                  <select data-testid="quote-incoterms" className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={q.incoterms} onChange={(e) => setQ({ ...q, incoterms: e.target.value })}>
                    <option>FOB</option><option>CIF</option><option>DDP</option><option>EXW</option><option>DAP</option>
                  </select>
                </div>
                <div><Label>Notes</Label><Textarea data-testid="quote-notes" rows={3} value={q.notes} onChange={(e) => setQ({ ...q, notes: e.target.value })} placeholder="Delivery timing, packaging, questions…" /></div>
                <Button data-testid="quote-submit" type="submit" disabled={busy} className="w-full">Request quote</Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
